import React, { VFC, useState } from 'react';
import styled from 'styled-components';
import {
  Container,
  Row,
  Col,
  useScreenClass,
} from 'react-grid-system';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useSettings } from '../contexts/settingsContext';
import Spacing from '../components/atoms/Spacing';
import Typography from '../components/atoms/Typography';
import PrimaryButton from '../components/molecules/PrimaryButton';
import { useReturnContext } from '../contexts/returnContext';
import FileInputComponent from '../components/molecules/FileInput';
import { tUpdateAndAddToReturnArgs } from '../contexts/zod/types';

interface Props {
  radius?: string;
}

interface Ivalues {
  file: File | null;
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SurroundingBorder = styled(Container) <Props>`
  border-radius: ${({ radius }) => radius};
  border: 1px solid #e0e0e0;
`;

const ErrorSpan = styled.span`
    color: red;
    display: block;
    font-size: 1.2rem;
  `;

const ReturnByFilePage: VFC = () => {
  const { cornerRadius, fontFamily } = useSettings();
  const { validateFile, addToReturns } = useReturnContext();
  const screenClass = useScreenClass();
  const [uploading, setUploading] = useState(false);
  const [theFile, setFile] = React.useState<File | undefined>(undefined);
  const [formErrors, setFormErrors] = useState([]);

  const shouldScale = ['lg', 'xl', 'xxl'].includes(screenClass);

  const onSubmit = async (values: Ivalues, resetForm: any) => {
    setUploading(true);
    if (values.file) {
      const response = await validateFile(values.file);
      if (response && response?.errors) {
        setFormErrors(response.errors);
      } else if (response && response?.data?.lines) {
        response.data.lines.forEach((line: tUpdateAndAddToReturnArgs) => {
          addToReturns(line, true);
          setFormErrors([]);
        });
      }
      setFile(undefined);
      resetForm();
    }
    setUploading(false);
  };

  const onChange = async (file: File, setFieldValue: any) => {
    setFile(file);
    setFieldValue('file', file);
  };

  const validationSchema = Yup.object().shape({
    file: Yup.mixed().required('File is required'),
  });

  return (
    <SurroundingBorder fluid radius={cornerRadius}>
      <Col style={{ backgroundColor: 'white' }}>
        <Spacing spacing={shouldScale ? 6 : 3} />
        <Row nogutter justify="center">
          <Typography variant="h3" translateKey="import_from_csv" />
        </Row>
        <Row nogutter justify="center">
          <Formik
            initialValues={{ file: null }}
            validationSchema={validationSchema}
            onSubmit={async (values: Ivalues, { resetForm }) => {
              onSubmit(values, resetForm);
            }}
          >
            {({ setFieldValue }) => (
              <StyledForm>
                <Spacing spacing={shouldScale ? 4 : 2} />
                <Field
                  id="file"
                  name="file"
                  type="file"
                  accept=".csv"
                  icon="upload"
                  updateFile={(e) => onChange(e, setFieldValue)}
                  file={theFile}
                  component={FileInputComponent}
                />
                <Spacing spacing={shouldScale ? 4 : 2} />
                <PrimaryButton
                  disabled={uploading}
                  loading={uploading}
                  type="submit"
                  color="primary"
                  translateKey="upload_file"
                  icon="upload"
                  iconSize="large"
                />
                <Spacing spacing={shouldScale ? 6 : 3} />
                {formErrors &&
                  formErrors.map((error) =>
                  (
                    <ErrorSpan
                      style={{ fontFamily }}
                      key={error.code}
                    >
                      {error.message}
                    </ErrorSpan>
                  ),
                  )}
                <Spacing spacing={shouldScale ? 6 : 3} />
              </StyledForm>
            )}
          </Formik>
        </Row>
      </Col>
    </SurroundingBorder>
  );
};

export default ReturnByFilePage;
